import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import { cn } from '@/lib/utils'

interface SessionDetail {
  session: { id: number; childId: number; startedAt: number; endedAt: number | null; exerciseType: string | null }
  summary: { totalAttempts: number; correctAttempts: number; accuracy: number; avgResponseMs: number }
  attempts: {
    id: number
    wordText: string
    correct: boolean
    responseMs: number | null
    attemptedAt: number
    attemptDetail: string | null
  }[]
}

function exerciseLabel(type: string | null): { emoji: string; name: string } {
  switch (type) {
    case 'WORD_DECODE': return { emoji: '🎤', name: 'Word Decode' }
    case 'SYNONYM_MATCH': return { emoji: '🔤', name: 'Synonym Match' }
    case 'SYNONYM_PAIRS': return { emoji: '🧩', name: 'Synonym Pairs' }
    case 'SPELLING': return { emoji: '📝', name: 'Spelling' }
    case 'NOTE_NAMING': return { emoji: '🎵', name: 'Note Naming' }
    case 'INTERVAL_RECOGNITION': return { emoji: '🎼', name: 'Interval Recognition' }
    case 'EAR_TRAINING': return { emoji: '👂', name: 'Ear Training' }
    default: return { emoji: '🎤', name: 'Practice' }
  }
}

function formatTimeOffset(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0:00'
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function parseDetail(type: string | null, detail: string | null): string | null {
  if (!detail) return null
  try {
    const parsed = JSON.parse(detail)
    if (type === 'WORD_DECODE' && parsed.transcript) {
      return `Said "${parsed.transcript}"`
    }
    if (type === 'SYNONYM_MATCH') {
      const parts: string[] = []
      if (parsed.chosenWordText) parts.push(`chose "${parsed.chosenWordText}"`)
      else if (parsed.chosenWordId) parts.push(`chose #${parsed.chosenWordId}`)
      if (parsed.correctWordText) parts.push(`correct "${parsed.correctWordText}"`)
      else if (parsed.correctWordId) parts.push(`correct #${parsed.correctWordId}`)
      return parts.length > 0 ? parts.join(', ') : null
    }
    if (type === 'SYNONYM_PAIRS') {
      const w1 = parsed.word1Text || `#${parsed.word1Id}`
      const w2 = parsed.word2Text || `#${parsed.word2Id}`
      if (parsed.word1Id && parsed.word2Id) {
        return `Paired "${w1}" with "${w2}"`
      }
    }
    if (type === 'SPELLING' && parsed.attemptedSpelling) {
      return `Typed "${parsed.attemptedSpelling}"`
    }
    if (type === 'NOTE_NAMING') {
      const parts: string[] = []
      if (parsed.noteName) parts.push(parsed.noteName)
      if (parsed.playerAnswer) parts.push(`answered ${parsed.playerAnswer}`)
      if (parsed.clef) parts.push(`${parsed.clef} clef`)
      return parts.length > 0 ? parts.join(', ') : null
    }
    if (type === 'INTERVAL_RECOGNITION' || type === 'EAR_TRAINING') {
      const parts: string[] = []
      if (parsed.intervalName) parts.push(parsed.intervalName)
      if (parsed.playerAnswer && parsed.playerAnswer !== parsed.intervalName) {
        parts.push(`answered ${parsed.playerAnswer}`)
      }
      if (parsed.clef) parts.push(`${parsed.clef} clef`)
      if (parsed.intervalType) parts.push(parsed.intervalType)
      return parts.length > 0 ? parts.join(', ') : null
    }
    return null
  } catch {
    return null
  }
}

export default function SessionDetailPage() {
  const { childId, sessionId } = useParams<{ childId: string; sessionId: string }>()
  const [data, setData] = useState<SessionDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const result = await apiFetch<SessionDetail>(
          `/dashboard/children/${childId}/sessions/${sessionId}`
        )
        if (!cancelled) setData(result)
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [childId, sessionId])

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">Loading session...</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">Session not found.</p>
      </div>
    )
  }

  const { session, summary, attempts } = data
  const { emoji, name } = exerciseLabel(session.exerciseType)
  const durationMs = session.endedAt ? session.endedAt - session.startedAt : 0

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <Link
        to={`/dashboard/children/${childId}/activity`}
        className="text-sm text-muted-foreground hover:text-foreground transition inline-flex items-center gap-1"
      >
        &larr; Back to activity
      </Link>

      <div className="flex items-center gap-3">
        <span className="text-3xl">{emoji}</span>
        <div>
          <h1 className="text-2xl font-bold">{name}</h1>
          <p className="text-sm text-muted-foreground">
            {new Date(session.startedAt).toLocaleDateString()} at{' '}
            {new Date(session.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </p>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg bg-muted/50 border border-border text-center">
          <p className="text-lg font-bold">{summary.totalAttempts}</p>
          <p className="text-xs text-muted-foreground">Attempts</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/50 border border-border text-center">
          <p className="text-lg font-bold text-primary">{Math.round(summary.accuracy * 100)}%</p>
          <p className="text-xs text-muted-foreground">Accuracy</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/50 border border-border text-center">
          <p className="text-lg font-bold">{Math.round(summary.avgResponseMs / 100) / 10}s</p>
          <p className="text-xs text-muted-foreground">Avg Response</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/50 border border-border text-center">
          <p className="text-lg font-bold">{formatTimeOffset(durationMs)}</p>
          <p className="text-xs text-muted-foreground">Duration</p>
        </div>
      </div>

      {/* Play-by-play */}
      <div className="space-y-1">
        <h2 className="text-lg font-semibold mb-3">Play-by-Play</h2>
        {attempts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No attempts recorded.</p>
        ) : (
          <div className="space-y-2">
            {attempts.map((a) => {
              const offset = a.attemptedAt - session.startedAt
              const detail = !a.correct ? parseDetail(session.exerciseType, a.attemptDetail) : null
              return (
                <div
                  key={a.id}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border',
                    a.correct ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'
                  )}
                >
                  <span className={cn('text-lg font-bold mt-0.5', a.correct ? 'text-green-600' : 'text-red-500')}>
                    {a.correct ? '✓' : '✗'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-bold">{a.wordText}</span>
                      {a.responseMs != null && (
                        <span className="text-xs text-muted-foreground">{Math.round(a.responseMs / 100) / 10}s</span>
                      )}
                    </div>
                    {detail && (
                      <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{formatTimeOffset(offset)}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
